import { useCallback, useEffect, useState } from "react";
import { Button, Grid2, Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { UserOrEmailMapping } from "../../features/Admin/typings/admin.types";
import { UserRole } from "../../features/Login/typings/auth.types";
import { getAdmin, setAdmin } from "../../utils/axios-requests";
import {
  getEmailsOfUserOrEmailMapping,
  getUserIdsOfUserOrEmailMapping
} from "../../utils/type/type.utils";
import AdminPicker from "./admin-picker.component";
import { StyledAdminSelectionPaper } from "./admin-selection-styled.component";

type P = {
  adminMode: string;
  options: UserOrEmailMapping[];
  enableNext?: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * @description Component that renders the admins interface.
 * @version 0.2.0
 */
const AdminSelection: ({ adminMode }: P) => JSX.Element = ({ enableNext, adminMode, options }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { keycloak } = useKeycloak();

  const {
    userRoles: { isTenantAdmin, isCompanyAdmin, isFinanceAdmin, isHrAdmin },
    userInformation
  } = useSelector((state: RootState) => state.login);

  const [selectedAdmins, setSelectedAdmins] = useState<UserOrEmailMapping[]>([]);

  const adminName = t(adminMode);

  const selectedMode = useCallback(() => {
    switch (adminMode) {
      case "Company Admin":
        return UserRole.isCompanyAdmin;
      case "HR Admin":
        return UserRole.isHrAdmin;
      case "Zone Booking Capable":
        return UserRole.isZoneBookingCapable;
      case "Equipment Admin":
        return UserRole.isEquipmentResponsible;
      case "Fleet Admin":
        return UserRole.isFleetAdmin;
      default:
        return UserRole.isZoneBookingCapable;
    }
  }, [adminMode]);

  /**
   * @description Effect that gets all users and current admin users when mounted.
   * @version 0.1.0
   */
  useEffect(() => {
    const getAdmins = async () => {
      const admins = await getAdmin(selectedMode());
      setSelectedAdmins(admins.data ?? []);
      if (enableNext && admins.data.length) {
        enableNext(true);
      }
    };

    getAdmins();
  }, [isCompanyAdmin, isTenantAdmin, isFinanceAdmin, isHrAdmin]);

  /**
   * @description Function that submits the chosen users as new admins to the API.
   * @version 0.1.0
   */
  const submitAdminRights = () => {
    const data = {
      userIds: getUserIdsOfUserOrEmailMapping(selectedAdmins),
      adminType: selectedMode(),
      emails: getEmailsOfUserOrEmailMapping(selectedAdmins)
    };
    setAdmin(data)
      .then(() => {
        if (enableNext && selectedAdmins.some(user => user.userId === userInformation.sub)) {
          enableNext(true);
          keycloak.login();
        } else if (enableNext) enableNext(false);
        enqueueSnackbar(t("EditedAdminModeUsers", { adminMode }), { variant: "success" });
      })
      .catch(() => {
        enqueueSnackbar(t("ErrorProvidingRights", { adminMode }), {
          variant: "error"
        });
      });
  };

  return (
    <StyledAdminSelectionPaper data-testid="admin-select-paper">
      <Grid2 container sx={{ width: "100%", height: "100%", alignContent: "space-between" }}>
        <Grid2 sx={{ width: "100%" }}>
          <Typography variant={"h5"} sx={{ mb: 1 }}>
            {adminName}
          </Typography>
          <AdminPicker
            handleChange={(
              e: React.ChangeEvent<UserOrEmailMapping>,
              values: UserOrEmailMapping[]
            ) => setSelectedAdmins(values)}
            values={selectedAdmins}
            placeholder={t("AddAdminMode", { adminName })}
            options={options}
          />
        </Grid2>

        <Grid2 sx={{ mt: 1 }}>
          <Button data-testid={"submit"} color="primary" onClick={submitAdminRights}>
            {t("Set New Admins")}
          </Button>
        </Grid2>
      </Grid2>
    </StyledAdminSelectionPaper>
  );
};

export default AdminSelection;
